import React, { useState, useEffect, useRef, useMemo } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { FormattedMessage } from 'react-intl';

import { DEFAULT_XM_LOGO_PATH } from '@xmatters/common/constants/image';

import CompatibilityWarning, {
  isCompatibleBrowser,
} from './components/CompatibilityWarning';
import ContactSupport from './components/ContactSupport';

import styles from './Login.module.scss';

export const LOGGING_IN = 'LOGGING_IN';
export default function Login({
  companyName,
  customLogoUrl,
  errorMessage,
  isSessionTimeout,
  isGuestRegistrationAllowed,
  isRelogin,
  refererUrl,
  disclaimer,
  privacyLink,
  tosLink,
  defaultUsername,
  supportUrl,
  titleText,
  hideAttribution,
  displayFullScreen,
  productTier,
}) {
  const [username, setUsername] = useState(defaultUsername);
  const [password, setPassword] = useState('');
  const [isDisabled, setIsDisabled] = useState(false);
  const usernameRef = useRef(null);
  const passwordRef = useRef(null);

  useEffect(() => {
    if (usernameRef.current.value) {
      passwordRef.current.focus();
    } else {
      usernameRef.current.focus();
    }
  }, []);

  const defaultXmLogoPath = useMemo(
    () => `${DEFAULT_XM_LOGO_PATH}?${Date.now()}`,
    [],
  );

  const handleInputChange = ({ target: { name, value } }) => {
    if (name === 'username') setUsername(value);
    if (name === 'password') setPassword(value);
  };

  const handleSubmit = () => {
    window.parent.postMessage({
      type: 'LOGGING_IN',
    });
    window.localStorage.setItem('xmLoginState', 'LOGGING_IN');
    setIsDisabled(true);
  };

  const PAID_TOS_URL =
    'https://www.xmatters.com/policy/hosted-service-subscription-policy/';
  const FREE_TOS_URL = 'https://www.xmatters.com/policy/terms-conditions/';
  const TRIAL_TOS_URL =
    'https://www.xmatters.com/policy/self-service-subscription-policy/';

  const getTosUrl = () => {
    if (tosLink) return tosLink;
    switch (productTier) {
      case 'TRIAL':
        return TRIAL_TOS_URL;
      case 'FREE':
        return FREE_TOS_URL;
      case 'PAID':
      default:
        return PAID_TOS_URL;
    }
  };

  const showError = errorMessage || isSessionTimeout;
  const errMessage = isSessionTimeout ? (
    <FormattedMessage id="LOG_IN_SESSION_EXPIRED" />
  ) : (
    // eslint-disable-next-line react/no-danger
    <div dangerouslySetInnerHTML={{ __html: unescape(errorMessage) }} />
  );

  const forgotPasswordLink = '/xmatters/passwords/reset';

  return (
    <div
      className={classnames(styles.container, {
        [styles.fullscreen]: displayFullScreen,
      })}
    >
      <div className={styles.content}>
        <div
          className={classnames(styles.main, {
            [styles.relogin]: isRelogin,
          })}
        >
          <div className={styles.loginContent}>
            <div className={styles.logo}>
              {customLogoUrl ? (
                <img
                  src={customLogoUrl}
                  alt={companyName}
                  className={styles.logoImage}
                />
              ) : (
                <a
                  href="https://xmatters.com"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <img src={defaultXmLogoPath} alt={companyName} width="186" />
                </a>
              )}
            </div>
            {!!titleText && (
              <div className={styles.title}>
                <span>{titleText}</span>
              </div>
            )}

            {isRelogin && (
              <>
                <div className={styles.title}>
                  <span>
                    <FormattedMessage id="LOG_IN" />
                  </span>
                </div>
                <div className={styles.description}>
                  <span>
                    <FormattedMessage id="RELOGIN_DESCRIPTION" />
                  </span>
                </div>
              </>
            )}

            {showError && (
              <div className={styles.error}>
                <span>{errMessage}</span>
              </div>
            )}
            {isCompatibleBrowser(navigator.userAgent) || isRelogin || (
              <div className={styles.error}>
                <CompatibilityWarning />
              </div>
            )}
            <div className={styles.loginForm}>
              <form method="post" id="loginForm" onSubmit={handleSubmit}>
                <input
                  type="hidden"
                  id="company"
                  name="companyName"
                  value={companyName}
                />
                {!!refererUrl && (
                  <input type="hidden" name="refererURL" value={refererUrl} />
                )}
                <div className={styles.formRow}>
                  <label htmlFor="username">
                    {isGuestRegistrationAllowed ? (
                      <FormattedMessage id="LOG_IN_EMAIL" />
                    ) : (
                      <FormattedMessage id="LOG_IN_USERNAME" />
                    )}
                  </label>
                  <input
                    onChange={handleInputChange}
                    ref={usernameRef}
                    type="text"
                    id="username"
                    name="username"
                    value={username}
                    readOnly={isDisabled}
                    maxLength="100"
                    data-testid="login-form-username"
                  />
                </div>
                <div className={styles.formRow}>
                  <label htmlFor="password">
                    <FormattedMessage id="LOG_IN_PASSWORD" />
                  </label>
                  <input
                    onChange={handleInputChange}
                    ref={passwordRef}
                    type="password"
                    id="password"
                    name="password"
                    value={password}
                    autoComplete="off"
                    readOnly={isDisabled}
                    data-testid="login-form-password"
                  />
                </div>
                {forgotPasswordLink && (
                  <div
                    className={styles.forgotPassword}
                    data-testid="login-form-forgot-password"
                  >
                    <span className={styles.formText}>
                      <a
                        href={forgotPasswordLink}
                        target={isRelogin ? '_blank' : '_self'}
                      >
                        <FormattedMessage id="LOG_IN_FORGOT_PASSWORD" />
                      </a>
                    </span>
                  </div>
                )}
                <div className={styles.formRow}>
                  <button
                    id="submitButton"
                    disabled={isDisabled}
                    type="submit"
                    data-testid="login-form-submit-button"
                  >
                    <FormattedMessage id="LOG_IN_BUTTON_LABEL" />
                  </button>
                  <div
                    className={styles.terms}
                    data-testid="login-form-privacy-tos"
                  >
                    <FormattedMessage
                      id="LOG_IN_TERMS_AGREEMENT"
                      values={{
                        // eslint-disable-next-line react/display-name
                        tosLink: chunks => (
                          <a
                            href={getTosUrl()}
                            target="_blank"
                            rel="noreferrer"
                          >
                            {chunks}
                          </a>
                        ),
                        // eslint-disable-next-line react/display-name
                        privacyLink: chunks => (
                          <a
                            href={privacyLink}
                            target="_blank"
                            rel="noreferrer"
                          >
                            {chunks}
                          </a>
                        ),
                      }}
                    />
                  </div>
                </div>
              </form>
            </div>
          </div>
          {!!supportUrl && (
            <div className={styles.contactSupport}>
              <ContactSupport supportUrl={supportUrl} />
            </div>
          )}
        </div>
        {!isRelogin && (
          <div className={styles.footer}>
            {!hideAttribution && (
              <div className={styles.poweredBy}>
                {companyName} <FormattedMessage id="LOG_IN_POWERED_BY" />{' '}
                <a
                  href="https://xmatters.com"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  xMatters, inc.
                </a>
              </div>
            )}
            <div className={styles.disclaimer}>{disclaimer}</div>
          </div>
        )}
      </div>
    </div>
  );
}

Login.propTypes = {
  companyName: PropTypes.string.isRequired,
  customLogoUrl: PropTypes.string.isRequired,
  isGuestRegistrationAllowed: PropTypes.bool.isRequired,
  errorMessage: PropTypes.string.isRequired,
  isRelogin: PropTypes.bool.isRequired,
  isSessionTimeout: PropTypes.bool.isRequired,
  refererUrl: PropTypes.string.isRequired,
  disclaimer: PropTypes.string.isRequired,
  privacyLink: PropTypes.string.isRequired,
  tosLink: PropTypes.string.isRequired,
  defaultUsername: PropTypes.string.isRequired,
  supportUrl: PropTypes.string.isRequired,
  titleText: PropTypes.string.isRequired,
  hideAttribution: PropTypes.bool.isRequired,
  displayFullScreen: PropTypes.bool.isRequired,
  productTier: PropTypes.string.isRequired,
};
